import { useEffect, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import PageHeader from "../../components/PageHeader";
import { Link } from "react-router-dom";

type Report = {
  id: string;
  reporterUid: string;
  targetUid: string;
  targetName: string;
  reason: string;
  note?: string;
  roomCode?: string;
  status: string;
  createdAtMs: number;
};

export default function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [filter, setFilter] = useState<"open" | "resolved">("open");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      query(
        collection(db, "reports"),
        where("status", "==", filter),
        orderBy("createdAt", "desc"),
        limit(100)
      ),
      (snap) => {
        setReports(
          snap.docs.map((d) => {
            const x = d.data();
            return {
              id: d.id,
              reporterUid: x.reporterUid,
              targetUid: x.targetUid,
              targetName: x.targetName ?? "—",
              reason: x.reason,
              note: x.note,
              roomCode: x.roomCode,
              status: x.status,
              createdAtMs: x.createdAt?.toMillis?.() ?? Date.now(),
            };
          })
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return unsub;
  }, [filter]);

  return (
    <div>
      <PageHeader
        title="Şikayetler"
        subtitle={`${reports.length} kayıt${filter === "open" ? " (açık)" : " (kapalı)"}`}
        actions={
          <div className="inline-flex rounded-xl bg-surface border border-stroke p-1">
            <TabBtn active={filter === "open"} onClick={() => setFilter("open")}>
              Açık
            </TabBtn>
            <TabBtn
              active={filter === "resolved"}
              onClick={() => setFilter("resolved")}
            >
              Çözülmüş
            </TabBtn>
          </div>
        }
      />

      {loading ? (
        <div className="card p-8 text-textMuted">Yükleniyor…</div>
      ) : reports.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="text-5xl mb-2">🎉</div>
          <div className="font-extrabold text-lg mb-1">Kuyruk temiz</div>
          <div className="text-sm text-textMuted">
            {filter === "open"
              ? "Bekleyen şikayet yok."
              : "Henüz çözüme kavuşan şikayet yok."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-4 py-1.5 rounded-lg text-xs font-bold transition",
        active
          ? "bg-primary text-white shadow"
          : "text-textMuted hover:text-textPrimary",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function ReportCard({ report }: { report: Report }) {
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  /**
   * Kararı yazar.
   *
   * `resolveReport` / `setUserBanned` callable'ları yerine doğrudan
   * Firestore: bu projede callable'ların Cloud Run servisi kimliksiz çağrıya
   * kapalı olduğu için istek fonksiyona ulaşmadan 403 alıyordu — düğmeler
   * her seferinde hata veriyordu. İki koleksiyona da yazma yetkisi
   * kurallarda yöneticiye açık.
   */
  const resolve = async (decision: "dismissed" | "warned" | "banned") => {
    setWorking(true);
    try {
      const by = auth.currentUser?.uid ?? null;
      if (decision === "banned") {
        // Önce yasak, sonra rapor: ban başarısız olursa rapor açık kalsın ve
        // kuyrukta tekrar karşımıza çıksın.
        await updateDoc(doc(db, "users", report.targetUid), {
          banned: true,
          banReason: `Şikayet ${report.id}: ${report.reason}`,
          bannedAt: serverTimestamp(),
          bannedBy: by,
        });
      }
      await updateDoc(doc(db, "reports", report.id), {
        status: "resolved",
        decision,
        decisionNote: "",
        resolvedBy: by,
        resolvedAt: serverTimestamp(),
      });
      setDone(decision);
    } catch (e) {
      alert("İşlem başarısız: " + (e as Error).message);
    } finally {
      setWorking(false);
    }
  };

  if (done) {
    return (
      <div className="card p-4 text-sm text-textMuted italic">
        ✓ Karar: {decisionLabel(done)} — kuyruktan çıkarıldı
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="pill bg-danger/15 text-danger border border-danger/30">
              {translateReason(report.reason)}
            </span>
            {report.roomCode && (
              <span className="pill bg-surfaceHigh text-textMuted">
                oda #{report.roomCode}
              </span>
            )}
            <span className="text-xs text-textMuted">
              {timeAgo(report.createdAtMs)}
            </span>
          </div>
          <div className="font-extrabold text-lg truncate">
            {report.targetName}
          </div>
          <div className="text-xs text-textMuted font-mono truncate">
            {report.targetUid}
          </div>
        </div>
        <Link
          to={`/admin/users/${report.targetUid}`}
          className="btn-ghost text-xs"
        >
          Profili aç →
        </Link>
      </div>

      {report.note && report.note.trim().length > 0 && (
        <div className="rounded-xl bg-bgDeep/60 border border-stroke p-3 text-sm text-textSecondary mb-3 whitespace-pre-wrap">
          <div className="text-[10px] font-bold tracking-widest uppercase text-textMuted mb-1">
            Raporlayanın notu
          </div>
          {report.note}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          disabled={working}
          onClick={() => resolve("dismissed")}
          className="btn-outline text-sm"
        >
          Reddet
        </button>
        <button
          disabled={working}
          onClick={() => resolve("warned")}
          className="btn-outline text-sm"
        >
          Uyar
        </button>
        <button
          disabled={working}
          onClick={() => {
            if (
              confirm(
                `${report.targetName} oyuncusunu tamamen YASAKLA? Bu işlem hesabı disable eder.`
              )
            )
              resolve("banned");
          }}
          className="btn-danger text-sm"
        >
          Yasakla
        </button>
      </div>
    </div>
  );
}

function decisionLabel(d: string) {
  return d === "dismissed"
    ? "reddedildi"
    : d === "warned"
    ? "uyarıldı"
    : d === "banned"
    ? "yasaklandı"
    : d;
}

function translateReason(r: string) {
  switch (r) {
    case "inappropriateName":
      return "Uygunsuz isim";
    case "offensiveBehavior":
      return "Saldırgan davranış";
    case "cheating":
      return "Hile";
    case "spam":
      return "Spam / bot";
    case "other":
      return "Diğer";
    default:
      return r;
  }
}

function timeAgo(ms: number) {
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  return `${Math.floor(hr / 24)} gün önce`;
}
